import { useEffect, useMemo, useState } from 'react';

type Order = 'asc' | 'desc' | 'none';

export type Sorting = {
  orderBy: string;
  order: Order;
};

export const getNextSorting = (
  previousSorting: Sorting,
  field: string,
): Sorting => {
  if (previousSorting.orderBy === field && previousSorting.order === 'desc') {
    return { orderBy: field, order: 'asc' as const };
  }

  if (previousSorting.orderBy === field && previousSorting.order === 'asc') {
    return { orderBy: '', order: 'none' as const };
  }

  return { orderBy: field, order: 'desc' as const };
};

export const useSorting = (initialSorting: Sorting) => {
  const [tableOrder, setTableOrder] = useState<Sorting>(initialSorting);
  const { order: initialOrder, orderBy: initialOrderBy } = initialSorting;

  useEffect(() => {
    setTableOrder(previousSorting =>
      previousSorting.orderBy === initialOrderBy &&
      previousSorting.order === initialOrder
        ? previousSorting
        : { orderBy: initialOrderBy, order: initialOrder },
    );
  }, [initialOrder, initialOrderBy]);

  return useMemo(() => {
    const sort = (field: string) => {
      setTableOrder(previousSorting => getNextSorting(previousSorting, field));
    };

    const isSortingActive = (field: string) => {
      return tableOrder.orderBy === field && tableOrder.order !== 'none';
    };

    const getSortingDirection = (field: string) => {
      if (field === tableOrder.orderBy) {
        return tableOrder.order === 'none' ? undefined : tableOrder.order;
      }

      return 'desc';
    };

    const orderBy =
      tableOrder.order !== 'none'
        ? `${tableOrder.orderBy}:${tableOrder.order}`
        : 'createTime:desc';

    return { tableOrder, sort, isSortingActive, getSortingDirection, orderBy };
  }, [tableOrder]);
};
